// src/pages/Headquarter/AddHeadquarter.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import headquarterService from '../../services/headquarter.service';
import regionService from '../../services/region.service';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const AddHeadquarter = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    companyId: '',
    regionId: '',
  });

  const [companies, setCompanies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      // Set user's company and fetch regions
      const userCompanyId = user?.companyId || '';
      setFormData(prev => ({ ...prev, companyId: userCompanyId }));
      if (userCompanyId) {
        fetchRegions(userCompanyId);
      }
    }
  }, [isSuperAdmin, user]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.success && response.data) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchRegions = async (companyId) => {
    try {
      const response = await regionService.getAllRegions(companyId);
      if (response.success && response.data) {
        setRegions(response.data);
      } else {
        setRegions([]);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // When company changes, fetch regions
    if (name === 'companyId' && value) {
      fetchRegions(value);
      setFormData(prev => ({ ...prev, regionId: '' })); // Reset region
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (isSuperAdmin && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    if (!formData.regionId) {
      newErrors.regionId = 'Region is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Headquarter name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        regionId: parseInt(formData.regionId),
      };

      if (isSuperAdmin) {
        payload.companyId = parseInt(formData.companyId);
      }

      const response = await headquarterService.createHeadquarter(payload);

      if (response.success) {
        alert('Headquarter added successfully!');
        navigate('/headquarter/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add headquarter');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-headquarter-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/headquarter/view">Headquarters</a>
          </li>
          <li className="breadcrumb-item active">Add Headquarter</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Headquarter</h4>
         </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Headquarter Information */}
          <div className="form-section">
                   
            <div className="form-row">
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">
                    Select Company <span className="required">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-input ${errors.companyId ? 'error' : ''}`}
                    value={formData.companyId}
                    onChange={handleChange}
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {errors.companyId && (
                    <span className="error-message">{errors.companyId}</span>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Select Region <span className="required">*</span>
                </label>
                <select
                  name="regionId"
                  className={`form-input ${errors.regionId ? 'error' : ''}`}
                  value={formData.regionId}
                  onChange={handleChange}
                  disabled={isSuperAdmin && !formData.companyId}
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
                {errors.regionId && (
                  <span className="error-message">{errors.regionId}</span>
                )}
                {isSuperAdmin && !formData.companyId && (
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Please select a company first
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Headquarter Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter headquarter name (e.g., Delhi HQ)"
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/headquarter/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Headquarter...' : 'Add Headquarter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddHeadquarter;